import React, { useEffect, useRef, useState } from 'react';
import { copyText, downloadJson, downloadText } from 'utils/download';
import { blockText, isBlockEmpty, traceBlocks, type TraceBlock } from 'utils/traceBlocks';
import type { DebugTrace, DebugStep2, DebugStep3 } from 'types/analysis';
import { engineLabel, v0ImageUrl, type Engine } from 'api/analysisApi';
import { Markdown } from 'components/common/Markdown';
import './trace.css';

/** A collapsible input/output block: header with View/Download, expandable body.
 *  kind='markdown' renders a formatted preview (tables, headings); Copy/Download
 *  still operate on the raw source text. */
function DataBlock({
  label,
  kind,
  value,
  downloadName,
  defaultOpen = false,
}: {
  label: string;
  kind: 'json' | 'text' | 'markdown';
  value: unknown;
  downloadName: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);
  const copyResetRef = useRef<ReturnType<typeof setTimeout>>();
  const text = blockText(kind, value);
  const empty = isBlockEmpty(kind, value);

  const onDownload = () => {
    if (kind === 'json') downloadJson(downloadName, value);
    else downloadText(`${downloadName}.txt`, text);
  };

  const onCopy = async () => {
    const ok = await copyText(text);
    if (!ok) return;
    setCopied(true);
    if (copyResetRef.current) clearTimeout(copyResetRef.current);
    copyResetRef.current = setTimeout(() => setCopied(false), 1500);
  };

  useEffect(() => () => clearTimeout(copyResetRef.current), []);

  return (
    <div className="block">
      <div className="block-head">
        <span className="block-label">{label}</span>
        <div className="block-actions">
          {!empty && (
            <button className="block-btn" type="button" onClick={() => setOpen((o) => !o)}>
              {open ? 'Hide' : 'View'}
            </button>
          )}
          {!empty && (
            <button className="block-btn" type="button" onClick={onCopy}>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          )}
          {!empty && (
            <button className="block-btn" type="button" onClick={onDownload}>
              ↓ {kind === 'json' ? 'JSON' : 'TXT'}
            </button>
          )}
        </div>
      </div>
      {empty && <div className="block-empty">— empty —</div>}
      {open && !empty && kind === 'markdown' && (
        <div className="block-md">
          <Markdown>{text.length > 80_000 ? text.slice(0, 80_000) + '\n\n… (truncated — use ↓ for full)' : text}</Markdown>
        </div>
      )}
      {open && !empty && kind !== 'markdown' && (
        <pre className={`block-pre ${kind}`}>
          {text.length > 80_000 ? text.slice(0, 80_000) + '\n… (truncated — use ↓ for full)' : text}
        </pre>
      )}
    </div>
  );
}

/** One DataBlock per trace block; `key` doubles as the per-block download name. */
function Blocks({ blocks, tag }: { blocks: TraceBlock[]; tag: string }) {
  return (
    <>
      {blocks.map((b) => (
        <DataBlock key={b.key} label={b.label} kind={b.kind} value={b.value} downloadName={`${b.key}_${tag}`} defaultOpen={b.defaultOpen} />
      ))}
    </>
  );
}

/** Format a USD cost for the trace chips. Sub-cent costs need more precision to
 *  stay distinguishable, so scale decimals to the magnitude. null/undefined → '—'. */
function formatCost(usd: number | null | undefined): string {
  if (usd == null) return '—';
  const decimals = usd >= 1 ? 2 : usd >= 0.01 ? 4 : 6;
  return `$${usd.toFixed(decimals)}`;
}

function Chip({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'warn' | 'mute' }) {
  return (
    <div className={`trace-chip${tone ? ` ${tone}` : ''}`}>
      <span className="chip-label">{label}</span>
      <span className="chip-value">{value}</span>
    </div>
  );
}

/** Token/timing chips shared by the Claude-writes-analysis step (v2 step3 or v3 step2). */
function ClaudeMetrics({ s }: { s: DebugStep2 | DebugStep3 }) {
  const tps = (s as DebugStep3).tokens_per_s;
  return (
    <div className="trace-metrics">
      {s.input_tokens != null && <Chip label="In" value={`${s.input_tokens} tok`} />}
      {s.output_tokens != null && <Chip label="Out" value={`${s.output_tokens} tok`} />}
      {s.ttft_s != null && <Chip label="TTFT" value={`${s.ttft_s.toFixed(2)}s`} />}
      {tps != null && <Chip label="Speed" value={`${tps.toFixed(0)} tok/s`} />}
    </div>
  );
}

interface AnalysisTraceProps {
  trace: DebugTrace;
  tag: string; // for download filenames, e.g. BTCUSDT_15m_binance
  engine: Engine;
}

export function AnalysisTrace({ trace, tag, engine }: AnalysisTraceProps) {
  const { step1, step2, step3, total_elapsed_s } = trace;
  const blocks = traceBlocks(trace);

  // Single-step engines (v0/v0_1 vision, v1 worker-data): one step IS the whole run
  // (no step2/step3) — render it on its own and bail before the multi-step logic
  // below (which assumes step2 exists).
  //
  // Two output shapes land here. v0/v0_1 return prose, and so do v1 rows written
  // before v1 moved to JSON — those stay in the audit log forever, so the shape is
  // detected per row rather than inferred from the engine.
  if (!step2) {
    return (
      <div className="trace">
        <div className="trace-summary">
          <Chip label="Engine" value={engineLabel(engine)} tone="good" />
          {total_elapsed_s != null && <Chip label="Total" value={`${total_elapsed_s.toFixed(1)}s`} />}
          {step1.total_tokens != null && <Chip label="Tokens" value={String(step1.total_tokens)} />}
          {step1.cost_usd !== undefined && <Chip label="Cost" value={formatCost(step1.cost_usd)} tone="warn" />}
          {step1.model && <Chip label="Model" value={step1.model} tone="mute" />}
          {step1.cache_read_input_tokens != null && (
            <Chip label="Cache read" value={`${step1.cache_read_input_tokens} tok`} tone="good" />
          )}
          {/* v0.1 / v1 inject a DB heatmap; v0 leaves these unset so the chip stays hidden. */}
          {step1.heatmap_used != null && (
            <Chip label="Heatmap" value={step1.heatmap_used ? 'used' : 'no'} tone={step1.heatmap_used ? 'good' : 'mute'} />
          )}
          {step1.source && <Chip label="Source" value={step1.source} tone="mute" />}
          {/* Only shown when it happened — a silent reformat retry is worth seeing. */}
          {step1.retried && <Chip label="JSON retry" value="yes" tone="warn" />}
        </div>

        <div className="trace-step">
          <div className="trace-step-head">
            <div className="trace-num">1</div>
            <div className="trace-titles">
              <div className="trace-title">{step1.title}</div>
              <div className="trace-desc">{step1.description}</div>
            </div>
            {step1.elapsed_s != null && <div className="trace-time">{step1.elapsed_s.toFixed(2)}s</div>}
          </div>

          {step1.images && step1.images.length > 0 && (
            <div className="trace-images">
              <div className="block-label">
                Uploaded screenshot{step1.images.length > 1 ? 's' : ''} ({step1.images.length})
              </div>
              <div className="trace-images-row">
                {step1.images.map((img, i) => {
                  const src = img.object_key ? v0ImageUrl(img.object_key) : img.data_url;
                  if (!src) {
                    return (
                      <div key={i} className="trace-thumb trace-thumb-missing" title={`${img.filename} — not stored`}>
                        <div className="trace-thumb-ph">not stored</div>
                        <span className="trace-thumb-name">{img.filename}</span>
                      </div>
                    );
                  }
                  return (
                    <a key={i} className="trace-thumb" href={src} target="_blank" rel="noopener noreferrer" title={`${img.filename} — open full size`}>
                      <img src={src} alt={img.filename} />
                      <span className="trace-thumb-name">{img.filename}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          <div className="trace-metrics">
            {step1.input_tokens != null && <Chip label="In" value={`${step1.input_tokens} tok`} />}
            {step1.output_tokens != null && <Chip label="Out" value={`${step1.output_tokens} tok`} />}
            {step1.ttft_s != null && <Chip label="TTFT" value={`${step1.ttft_s.toFixed(2)}s`} />}
          </div>

          <Blocks blocks={blocks.step1} tag={tag} />
        </div>
      </div>
    );
  }

  const isInContext = blocks.step2Shape === 'incontext'; // v3: Claude calcs + writes in one call

  // Token/model summary comes from whichever object Claude wrote the analysis in.
  const totalTokens = step3?.total_tokens ?? step2.total_tokens;
  const costUsd = step3?.cost_usd ?? step2.cost_usd;
  const model = step3?.model ?? step2.model;

  return (
    <div className="trace">
      {/* Run summary */}
      <div className="trace-summary">
        <Chip
          label="Engine"
          value={engineLabel(engine)}
          tone={engine === 'claude' ? 'warn' : 'good'}
        />
        {total_elapsed_s != null && <Chip label="Total" value={`${total_elapsed_s.toFixed(1)}s`} />}
        {totalTokens != null && <Chip label="Tokens" value={String(totalTokens)} />}
        {costUsd !== undefined && <Chip label="Cost" value={formatCost(costUsd)} tone="warn" />}
        {model && <Chip label="Model" value={model} tone="mute" />}
        <Chip
          label="Heatmap"
          value={step1.heatmap_used ? 'used' : 'no'}
          tone={step1.heatmap_used ? 'good' : 'mute'}
        />
        {step1.source && <Chip label="Source" value={step1.source} tone="mute" />}
      </div>

      {/* Step 1 — Data source */}
      <div className="trace-step">
        <div className="trace-step-head">
          <div className="trace-num">1</div>
          <div className="trace-titles">
            <div className="trace-title">{step1.title}</div>
            <div className="trace-desc">{step1.description}</div>
          </div>
          {step1.elapsed_s != null && <div className="trace-time">{step1.elapsed_s.toFixed(2)}s</div>}
        </div>
        {step1.images && step1.images.length > 0 && (
          <div className="trace-images">
            <div className="block-label">Uploaded screenshot{step1.images.length > 1 ? 's' : ''} ({step1.images.length})</div>
            <div className="trace-images-row">
              {step1.images.map((img, i) => {
                const src = img.object_key ? v0ImageUrl(img.object_key) : img.data_url;
                if (!src) {
                  return (
                    <div key={i} className="trace-thumb trace-thumb-missing" title={`${img.filename} — not stored`}>
                      <div className="trace-thumb-ph">not stored</div>
                      <span className="trace-thumb-name">{img.filename}</span>
                    </div>
                  );
                }
                return (
                  <a
                    key={i}
                    className="trace-thumb"
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`${img.filename} — open full size`}
                  >
                    <img src={src} alt={img.filename} />
                    <span className="trace-thumb-name">{img.filename}</span>
                  </a>
                );
              })}
            </div>
          </div>
        )}
        <Blocks blocks={blocks.step1} tag={tag} />
      </div>

      <div className="trace-arrow">↓</div>

      {/* Step 2 — engine-dependent */}
      <div className="trace-step">
        <div className="trace-step-head">
          <div className="trace-num">2</div>
          <div className="trace-titles">
            <div className="trace-title">{step2.title}</div>
            <div className="trace-desc">{step2.description}</div>
          </div>
          {step2.elapsed_s != null && <div className="trace-time">{step2.elapsed_s.toFixed(2)}s</div>}
        </div>

        {isInContext && <ClaudeMetrics s={step2} />}
        <Blocks blocks={blocks.step2} tag={tag} />
      </div>

      {/* Step 3 — only when the engine separates calc (step2) from analysis (step3) */}
      {step3 && (
        <>
          <div className="trace-arrow">↓</div>
          <div className="trace-step">
            <div className="trace-step-head">
              <div className="trace-num">3</div>
              <div className="trace-titles">
                <div className="trace-title">{step3.title}</div>
                <div className="trace-desc">{step3.description}</div>
              </div>
              {step3.elapsed_s != null && <div className="trace-time">{step3.elapsed_s.toFixed(2)}s</div>}
            </div>
            <ClaudeMetrics s={step3} />
            <Blocks blocks={blocks.step3} tag={tag} />
          </div>
        </>
      )}
    </div>
  );
}
